import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { FormValues, Participant, Device } from "../utils/meeting.utils";
import { format, parseISO } from "date-fns";
import type { Meeting } from "../types/meeting.type";

interface MeetingFormState {
  formData: Partial<FormValues>;
  isLoading: boolean;
  errors: Partial<Record<keyof FormValues, string>>;
}

// Helper function để tính toán default values dựa trên thời gian hiện tại
const getDefaultFormData = (): Partial<FormValues> => {
  const now = new Date();
  const currentDate = new Date(now);
  
  // Format ngày: DD/MM/YYYY
  const dateStr = format(currentDate, "dd/MM/yyyy");
  
  // Tính giờ bắt đầu: giờ hiện tại + 5 phút
  const startTime = new Date(now);
  startTime.setMinutes(startTime.getMinutes() + 5);
  const startTimeStr = `${String(startTime.getHours()).padStart(2, "0")}:${String(startTime.getMinutes()).padStart(2, "0")}`;
  
  // Tính giờ kết thúc: giờ bắt đầu + 1 tiếng
  const endTime = new Date(startTime);
  endTime.setHours(endTime.getHours() + 1);
  const endTimeStr = `${String(endTime.getHours()).padStart(2, "0")}:${String(endTime.getMinutes()).padStart(2, "0")}`;
  
  // Format ngày kết thúc: yyyy-MM-dd
  const recurrenceEndDateStr = format(currentDate, "yyyy-MM-dd");
  
  return {
    title: `Họp - ${dateStr}`,
    description: `Họp - ${dateStr}`,
    date: currentDate,
    startTime: startTimeStr,
    endTime: endTimeStr,
    roomMeetingId: "",
    participantHost: undefined,
    participants: [],
    devices: [],
    allowRecording: true,
    autoRecord: false,
    remindHostToJoin: true,
    allowScreenShare: true,
    lockAction: false,
    layout: 'custom',
    autoLayoutByMic: false,
    defaultMicOn: false,
    defaultCameraOn: false,
    cameraResolution: "1080p",
    waitingRoomEnable: false,
    requirePassword: false,
    password: "",
    maxParticipants: 10,
    isRecurring: false,
    recurrencePattern: undefined,
    recurrenceDays: [],
    recurrenceEndDate: recurrenceEndDateStr,
    notes: "",
    reminderMinutes: 10,
    type: "scheduled",
  };
};

const defaultFormData: Partial<FormValues> = {
  title: "",
  description: "",
  date: new Date(),
  startTime: "09:00",
  endTime: "10:00",
  roomMeetingId: "",
  participantHost: undefined,
  participants: [],
  devices: [],
  remindHostToJoin: true,
  allowScreenShare: true,
  lockAction: false,
  allowRecording: false,
  autoRecord: false,
  defaultMicOn: false,
  defaultCameraOn: false,
  cameraResolution: "1080p",
  waitingRoomEnable: false,
  requirePassword: false,
  password: "",
  maxParticipants: 10,
  isRecurring: false,
  recurrencePattern: undefined,
  recurrenceDays: [],
  recurrenceEndDate: undefined,
  notes: "",
  reminderMinutes: 10,
  type: "scheduled",
};

const initialState: MeetingFormState = {
  formData: defaultFormData,
  isLoading: false,
  errors: {},
};

const meetingFormSlice = createSlice({
  name: "meetingForm",
  initialState,
  reducers: {
    // Reset form về giá trị mặc định
    resetFormData(state) {
      state.formData = defaultFormData;
      state.errors = {};
    },

    // Initialize form với default values động
    initializeDefaultFormData(state) {
      state.formData = getDefaultFormData();
      state.errors = {};
    },

    // Cập nhật form data - action tổng hợp để update nhiều field cùng lúc
    updateFormData(state, action: PayloadAction<Partial<FormValues>>) {
      const updates = action.payload;
      if (updates.remindHostToJoin !== undefined) state.formData.remindHostToJoin = updates.remindHostToJoin;
      if (updates.allowScreenShare !== undefined) state.formData.allowScreenShare = updates.allowScreenShare;
      if (updates.lockAction !== undefined) state.formData.lockAction = updates.lockAction;

      if (updates.layout !== undefined) state.formData.layout = updates.layout;
      if (updates.autoLayoutByMic !== undefined) state.formData.autoLayoutByMic = updates.autoLayoutByMic;
      
      // Cập nhật các field - chỉ xóa error cho 2 trường required: title và date
      if (updates.title !== undefined) {
        state.formData.title = updates.title;
        delete state.errors.title;
      }
      if (updates.description !== undefined) {
        state.formData.description = updates.description;
      }
      if (updates.date !== undefined) {
        state.formData.date = updates.date;
        delete state.errors.date;
      }
      if (updates.startTime !== undefined) {
        state.formData.startTime = updates.startTime;
      }
      if (updates.endTime !== undefined) {
        state.formData.endTime = updates.endTime;
      }
      if (updates.roomMeetingId !== undefined) {
        state.formData.roomMeetingId = updates.roomMeetingId || "";
      }
      if (updates.participantHost !== undefined) {
        state.formData.participantHost = updates.participantHost;
      }
      if (updates.participants !== undefined) {
        state.formData.participants = updates.participants;
      }
      if (updates.devices !== undefined) {
        state.formData.devices = updates.devices;
      }
      if (updates.allowRecording !== undefined) {
        state.formData.allowRecording = updates.allowRecording;
      }
      if (updates.autoRecord !== undefined) {
        state.formData.autoRecord = updates.autoRecord;
      }
      if (updates.defaultMicOn !== undefined) {
        state.formData.defaultMicOn = updates.defaultMicOn;
      }
      if (updates.defaultCameraOn !== undefined) {
        state.formData.defaultCameraOn = updates.defaultCameraOn;
      }
      if (updates.cameraResolution !== undefined) {
        state.formData.cameraResolution = updates.cameraResolution;
      }
      if (updates.waitingRoomEnable !== undefined) {
        state.formData.waitingRoomEnable = updates.waitingRoomEnable;
      }
      if (updates.requirePassword !== undefined) {
        state.formData.requirePassword = updates.requirePassword;
      }
      if (updates.password !== undefined) {
        state.formData.password = updates.password;
      }
      if (updates.maxParticipants !== undefined) {
        state.formData.maxParticipants = updates.maxParticipants;
      }
      if (updates.isRecurring !== undefined) {
        state.formData.isRecurring = updates.isRecurring;
      }
      if (updates.recurrencePattern !== undefined) {
        state.formData.recurrencePattern = updates.recurrencePattern;
      }
      if (updates.recurrenceEndDate !== undefined) {
        state.formData.recurrenceEndDate = updates.recurrenceEndDate;
      }
      if (updates.recurrenceDays !== undefined) {
        state.formData.recurrenceDays = updates.recurrenceDays;
      }
      if (updates.reminderMinutes !== undefined) {
        state.formData.reminderMinutes = updates.reminderMinutes;
      }
      if (updates.type !== undefined) {
        state.formData.type = updates.type;
      }
    },

    // Set loading state
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    // Set errors
    setErrors(state, action: PayloadAction<Partial<Record<keyof FormValues, string>>>) {
      state.errors = { ...state.errors, ...action.payload };
    },

    // Set form data from Meeting object (for edit mode)
    setFormDataFromMeeting(state, action: PayloadAction<Meeting>) {
      const meeting = action.payload;
      const startDate = parseISO(meeting.startTime);
      const endDate = parseISO(meeting.endTime);
      
      // Convert participants
      const participants: Participant[] = meeting.participants
        .filter((p) => p.role !== "host")
        .map((p) => ({
          id: p.userId,
          name: p.fullName,
          email: p.email,
          role: p.role as "participant",
        }));

      // Find host
      const hostParticipant = meeting.participants.find((p) => p.role === "host");
      const participantHost: Participant | undefined = hostParticipant
        ? {
            id: hostParticipant.userId,
            name: hostParticipant.fullName,
            email: hostParticipant.email,
            role: "host",
          }
        : undefined;

      // Convert devices
      const devices: Device[] = meeting.devices.map((d) => ({
        id: d.deviceId,
        name: d.deviceName,
        type: "camera", // Default type
      }));

      state.formData = {
        title: meeting.title,
        description: meeting.description || "",
        date: startDate,
        startTime: format(startDate, "HH:mm"),
        endTime: format(endDate, "HH:mm"),
        roomMeetingId: meeting.roomMeetingId && meeting.roomMeetingId.trim() !== "" ? meeting.roomMeetingId : "",
        participantHost,
        participants,
        devices,
        allowRecording: meeting.allowRecording,
        autoRecord: meeting.autoRecord,
        remindHostToJoin: meeting.remindHostToJoin ?? true,
        allowScreenShare: meeting.allowScreenShare ?? true,
        lockAction: meeting.lockAction ?? false,
        layout: meeting.layout ?? 'custom',
        autoLayoutByMic: meeting.autoLayoutByMic ?? false,
        defaultMicOn: meeting.defaultMicOn,
        defaultCameraOn: false, // Not in Meeting type, use default
        cameraResolution: "1080p", // Not in Meeting type, use default
        waitingRoomEnable: false, // Not in Meeting type, use default
        requirePassword: false, // Not in Meeting type, use default
        password: "",
        maxParticipants: meeting.maxParticipants,
        isRecurring: meeting.isRecurring,
        recurrencePattern: meeting.recurrencePattern as "daily" | "weekly" | "monthly" | "custom" | undefined,
        recurrenceDays: meeting.recurrenceDays || [],
        recurrenceEndDate: meeting.recurrenceEndDate || undefined,
        notes: meeting.notes || "",
        reminderMinutes: meeting.reminderMinutes,
        type: "scheduled",
      };
      state.errors = {};
    },
  },
});

export const meetingFormActions = meetingFormSlice.actions;
export const meetingFormReducer = meetingFormSlice.reducer;

